import { VoiceToolSurface, VoiceUITool } from './types'
import type { PageToolSpec } from '../agent/types'

// One registry, two ways in (#1534). A page declares every control as a tool.
// The few a model needs all the time are `direct`: they sit in its tool list.
// The rest are `search`: the model finds them with ui_search and runs them
// with ui_call. The voice session (a 32-tool cap) and the Tables Insights pane
// (a chat-completion loop the browser drives) both reach the page this way, so
// a control the page describes once works in both.

export const UI_SEARCH = 'ui_search'
export const UI_CALL = 'ui_call'
const BRIDGE_NAMES = new Set([UI_SEARCH, UI_CALL])

/** At most this many tools come back from one ui_search, each with its schema. */
export const MAX_UI_MATCHES = 8

/** How a model reaches a tool. A click-confirmed tool is never direct. */
export function toolSurfaceOf(tool: VoiceUITool): VoiceToolSurface {
  if (tool.meta?.confirm === 'click') return 'search'
  return tool.meta?.surface ?? 'direct'
}

export interface UIToolMatch {
  name: string
  section?: string
  description: string
  parameters: Record<string, unknown>
}

export interface UISearchResult {
  count: number
  matches?: UIToolMatch[]
  more?: number
  sections?: Array<{ section: string; count: number }>
  hint?: string
}

const words = (s: string) => s.toLowerCase().split(/[^a-z0-9%]+/).filter(Boolean)

function sectionOf(t: VoiceUITool): string {
  return t.meta?.section || 'Other'
}

function sectionCounts(tools: VoiceUITool[]) {
  const counts = new Map<string, number>()
  for (const t of tools) counts.set(sectionOf(t), (counts.get(sectionOf(t)) ?? 0) + 1)
  return [...counts].map(([section, count]) => ({ section, count }))
}

function toMatch(t: VoiceUITool): UIToolMatch {
  return {
    name: t.name,
    ...(t.meta?.section ? { section: t.meta.section } : {}),
    description: t.description,
    parameters: t.parameters,
  }
}

// Score one tool against the query's words: a word in the tool's name counts
// most, then its keywords, then its section and description. A word that is
// only the start of a name word still counts ("decimal" finds set_decimals).
function score(t: VoiceUITool, queryWords: string[], phrase: string): number {
  const name = words(t.name.replace(/_/g, ' '))
  const keywords = (t.meta?.keywords ?? []).flatMap(words)
  const section = words(sectionOf(t))
  const description = t.description.toLowerCase()
  let total = 0
  for (const w of queryWords) {
    if (name.some((n) => n === w || n.startsWith(w) || (w.startsWith(n) && n.length >= 4))) total += 3
    if (keywords.some((k) => k === w || k.startsWith(w))) total += 2
    if (section.some((s) => s === w || s.startsWith(w))) total += 1
    if (description.includes(w)) total += 1
  }
  if (phrase.length > 3 && (description.includes(phrase) || (t.meta?.keywords ?? []).some((k) => k.toLowerCase() === phrase))) total += 2
  return total
}

/**
 * Rank a page's tools against what the user asked for. An empty query lists
 * the sections and how many tools each has; a section alone lists its tools.
 */
export function searchUITools(tools: VoiceUITool[], query: string, section?: string): UISearchResult {
  const wantSection = (section ?? '').trim().toLowerCase()
  const pool = tools
    .filter((t) => !BRIDGE_NAMES.has(t.name))
    .filter((t) => !wantSection || sectionOf(t).toLowerCase().startsWith(wantSection))
  const queryWords = words(query)

  if (queryWords.length === 0) {
    if (!wantSection) {
      return {
        count: pool.length,
        sections: sectionCounts(pool),
        hint: 'Call ui_search again with a few words for what the user wants, or with a section.',
      }
    }
    return {
      count: pool.length,
      matches: pool.slice(0, MAX_UI_MATCHES).map(toMatch),
      ...(pool.length > MAX_UI_MATCHES ? { more: pool.length - MAX_UI_MATCHES } : {}),
    }
  }

  const phrase = query.trim().toLowerCase()
  const ranked = pool
    .map((t, i) => ({ t, i, s: score(t, queryWords, phrase) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((x) => x.t)

  if (ranked.length === 0) {
    return {
      count: 0,
      matches: [],
      sections: sectionCounts(pool),
      hint: 'Nothing matched. Try other words, or a section. If the page has no tool for it, say so.',
    }
  }
  return {
    count: ranked.length,
    matches: ranked.slice(0, MAX_UI_MATCHES).map(toMatch),
    ...(ranked.length > MAX_UI_MATCHES ? { more: ranked.length - MAX_UI_MATCHES } : {}),
  }
}

const json = (v: unknown) => JSON.stringify(v)

/** The `args` of a ui_call: an object, or a JSON string of one. */
export function callArgs(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return { ...(raw as Record<string, unknown>) }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as Record<string, unknown>
    } catch {
      // fall through to no arguments
    }
  }
  return {}
}

/**
 * How the page agent confirms a tool: its own rule, and a costly tool (spends
 * AI credits, starts a run) as a click tool, so only the person's Confirm
 * runs it (plan D11), never the model's own `confirmed`.
 */
export function pageConfirmRule(tool: VoiceUITool): 'ask' | 'click' | undefined {
  return tool.meta?.confirm ?? (tool.meta?.costly ? 'click' : undefined)
}

/**
 * What the page agent is told about every tool it must not confirm itself.
 * Without it the agent asked in words and then the card asked again.
 */
export const PAGE_CONFIRM_NOTE =
  'The page shows the person a Confirm card for this call; do not ask them first and do not pass confirmed. ' +
  'An ok result with confirmed_by_person: true means they clicked Confirm and the change is done: report from the result. ' +
  'The error "The person declined." means no: accept it and do not call again.'

// The schema without `confirmed`: only the person's click supplies it.
function withoutConfirmed(parameters: Record<string, unknown>): Record<string, unknown> {
  const properties = parameters.properties as Record<string, unknown> | undefined
  if (!properties || !('confirmed' in properties)) return parameters
  const rest = { ...properties }
  delete rest.confirmed
  const out: Record<string, unknown> = { ...parameters, properties: rest }
  if (Array.isArray(parameters.required)) out.required = parameters.required.filter((r) => r !== 'confirmed')
  return out
}

/**
 * A page tool as the page agent is sent it. An ask, click or costly tool waits
 * for the person's Confirm: it is described with agentDescription (when it has
 * one) and PAGE_CONFIRM_NOTE, and offered no `confirmed`. Built from the tool's
 * static fields only, so the list is the same every turn (D3).
 */
export function toPageToolSpec(tool: VoiceUITool): PageToolSpec {
  if (pageConfirmRule(tool) === undefined) {
    return { name: tool.name, description: tool.description, parameters: tool.parameters }
  }
  return {
    name: tool.name,
    description: `${(tool.meta?.agentDescription ?? tool.description).trimEnd()} ${PAGE_CONFIRM_NOTE}`,
    parameters: withoutConfirmed(tool.parameters),
    confirm: 'click',
  }
}

/**
 * The page agent's tool list (Douglas plan D3): every page tool, `search`
 * ones included, never the bridge, sorted by name. The page agent sends this
 * with every turn, so it must not change with what is open on the page.
 */
export function toPageToolSpecs(tools: VoiceUITool[]): PageToolSpec[] {
  return tools
    .filter((t) => !BRIDGE_NAMES.has(t.name) && t.meta?.category !== 'bridge')
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    .map(toPageToolSpec)
}

export interface PageToolOutcome {
  ok: boolean
  result?: unknown
  error?: string
}

// The tool's own answer, as the string it returns; a throw becomes {error}.
async function executeTool(tool: VoiceUITool, args: Record<string, unknown>): Promise<string> {
  try {
    return String(await tool.execute(args))
  } catch (e) {
    return json({ error: e instanceof Error ? e.message : `${tool.name} failed` })
  }
}

// Only the person's click confirms an ask or click tool: a model's own
// `confirmed` is dropped, and `confirmedByUser` (the Confirm button) adds it.
function confirmArgs(tool: VoiceUITool, args: Record<string, unknown>, confirmedByUser: boolean, rule = tool.meta?.confirm): Record<string, unknown> {
  const a = { ...args }
  if (rule === 'ask' || rule === 'click') delete a.confirmed
  if (confirmedByUser) a.confirmed = true
  return a
}

/**
 * Run one page tool for the page agent. A JSON `{error}` answer, or a throw,
 * is `ok: false`; any other answer is the result, parsed when it is JSON. A
 * result the person's Confirm produced carries `confirmed_by_person: true`,
 * so the agent reports the change instead of asking about it again.
 */
export async function runPageTool(
  tool: VoiceUITool,
  args: Record<string, unknown>,
  opts: { confirmedByUser?: boolean } = {},
): Promise<PageToolOutcome> {
  const outcome = outcomeOf(await executeTool(tool, confirmArgs(tool, args, !!opts.confirmedByUser, pageConfirmRule(tool))))
  const r = outcome.result
  if (opts.confirmedByUser && outcome.ok && r && typeof r === 'object' && !Array.isArray(r)) {
    return { ...outcome, result: { ...(r as Record<string, unknown>), confirmed_by_person: true } }
  }
  return outcome
}

function outcomeOf(raw: string): PageToolOutcome {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return { ok: true, result: raw }
  }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'error' in parsed) {
    const e = (parsed as { error: unknown }).error
    if (e !== undefined && e !== null && e !== false) return { ok: false, error: typeof e === 'string' ? e : json(e) }
  }
  return { ok: true, result: parsed }
}

export interface UIBridge {
  /** ui_search and ui_call, as voice tools. */
  tools: VoiceUITool[]
  /** Run ui_search or ui_call by name, as a model asked. */
  execute: (name: string, args: Record<string, unknown>) => Promise<string>
  /**
   * Run a page tool with confirmed=true. Only the pane's Confirm button calls
   * this: it is the user's click, which no model can supply.
   */
  confirm: (toolName: string, args: Record<string, unknown>) => Promise<string>
  /** The page tool with this name, if there is one. */
  find: (toolName: string) => VoiceUITool | undefined
}

/**
 * The bridge over a page's live tools. `getTools` is read on every call, so
 * the bridge always runs the latest render's tools.
 */
export function createUIBridge(getTools: () => VoiceUITool[]): UIBridge {
  const pageTools = () => getTools().filter((t) => !BRIDGE_NAMES.has(t.name))
  const find = (name: string) => pageTools().find((t) => t.name === name)

  const tools: VoiceUITool[] = [
    {
      name: UI_SEARCH,
      meta: { category: 'bridge' },
      description:
        "Search the controls of the page the user is looking at. Returns up to 8 page tools, best first, each with its name, what it does and its parameter schema. " +
        "Call it with an empty query to list the page's sections. Use it whenever no tool in your list fits what the user wants on this page.",
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: "Words for what the user wants done, e.g. 'filter', 'decimals', 'rename table'. Empty lists the sections." },
          section: { type: 'string', description: 'Limit the search to one section.' },
        },
        required: ['query'],
        additionalProperties: false,
      },
      execute: (args) => json(searchUITools(pageTools(), String(args.query ?? ''), args.section ? String(args.section) : undefined)),
    },
    {
      name: UI_CALL,
      meta: { category: 'bridge' },
      description:
        'Run one page tool by name with its arguments, exactly as ui_search described it. It changes what the user sees and returns the tool\'s own result. ' +
        'A result with needs_confirmation means the user must approve it: say so in one sentence and stop.',
      parameters: {
        type: 'object',
        properties: {
          tool: { type: 'string', description: 'The page tool name from ui_search.' },
          // No additionalProperties: false here — each page tool checks its own arguments.
          args: { type: 'object', description: "The tool's arguments, as its schema says. {} when it takes none." },
        },
        required: ['tool'],
      },
      execute: async (args) => {
        const name = String(args.tool ?? '').trim()
        if (!name) return json({ error: 'tool is required — call ui_search to find one' })
        if (BRIDGE_NAMES.has(name)) return json({ error: `${name} is not a page tool` })
        const tool = find(name)
        if (!tool) return json({ error: `no page tool called "${name}" — call ui_search to see what this page has` })
        const a = callArgs(args.args)
        // Only the user's click confirms a click-confirmed tool. An ask tool's
        // confirmed=true reaches here only after the pane's loop checked the
        // user said yes to that ask (uiToolLoop, confirmedByUser), so it stands.
        const askedAndAgreed = tool.meta?.confirm === 'ask' && a.confirmed === true
        return executeTool(tool, confirmArgs(tool, a, askedAndAgreed))
      },
    },
  ]

  return {
    tools,
    execute: async (name, args) => {
      const tool = tools.find((t) => t.name === name)
      if (!tool) return json({ error: `${name} is not a bridge tool` })
      return String(await tool.execute(args))
    },
    confirm: async (toolName, args) => {
      const tool = find(toolName)
      if (!tool) return json({ error: `${toolName} is not available on this page right now` })
      return executeTool(tool, confirmArgs(tool, args, true))
    },
    find,
  }
}
